/**
 * One report in the triage table.
 *
 * Owner and status are the only two things editable inline, because they are the only two the
 * weekly pass actually changes. Everything else — the description, the screenshots, the
 * conversation — is one click away in Slack, where it already lives and where the people who
 * reported it are already looking.
 */
import { ExternalLink, Image, MessageSquare, Paperclip } from 'lucide-react';
import type { FeedbackReport, OwnerId, TriagePatch, TriageOwner } from '../../services/feedbackTriageApi';
import { Avatar, PiaChip, PriorityChip, TypeChip } from './triageUi';
import { RADIUS, STATUS_OPTIONS, ageOf, reporterName } from './triageTokens';

const SELECT =
  `w-full ${RADIUS} border border-zinc-200 bg-white px-2 py-1.5 text-[12.5px] text-zinc-900 ` +
  'focus:outline-none focus:ring-2 focus:ring-blue-500';

interface Props {
  report: FeedbackReport;
  owners: TriageOwner[];
  busy: boolean;
  onPatch: (patch: TriagePatch) => void;
  onOpen: () => void;
}

export function ReportRow({ report, owners, busy, onPatch, onOpen }: Props) {
  const owner = owners.find((o) => o.id === report.owner);

  return (
    <div
      className={`grid items-center gap-3 border-t border-zinc-100 px-5 py-2.5 hover:bg-zinc-50 ${
        busy ? 'opacity-60' : ''
      }`}
      style={{ gridTemplateColumns: '54px minmax(0,1fr) 116px 70px 152px 150px 32px' }}
    >
      <div>
        <PriorityChip priority={report.priority} />
      </div>

      <div className="min-w-0">
        <button
          type="button"
          onClick={onOpen}
          className="block w-full truncate text-left text-[13.5px] font-semibold text-zinc-900 hover:text-blue-700 hover:underline"
          title={report.description || report.title}
        >
          {report.title}
        </button>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-zinc-500">
          <span className="truncate">{report.area}</span>
          <span aria-hidden>·</span>
          <span>{reporterName(report.reporter)}</span>
          {report.pia && (
            <>
              <span aria-hidden>·</span>
              <PiaChip pia={report.pia} />
            </>
          )}
          {report.replies > 0 && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1">
                <MessageSquare className="size-3" aria-hidden />
                {report.replies}
              </span>
            </>
          )}
          {report.screenshots > 0 && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1">
                <Image className="size-3" aria-hidden />
                {report.screenshots}
              </span>
            </>
          )}
          {/* Says the thread quotes a candidate, without repeating any of it here. */}
          {report.hasCandidateContext && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1" title="Quotes a candidate message">
                <Paperclip className="size-3" aria-hidden />
                candidate
              </span>
            </>
          )}
        </div>
      </div>

      <div>
        <TypeChip type={report.type} />
      </div>

      <div className="text-[12.5px] text-zinc-600">{ageOf(report.postedAt)}</div>

      <div className="flex items-center gap-2">
        {owner && <Avatar owner={owner.id} initials={owner.initials} />}
        {/* The automatic owner (page default or Pia) counts as assigned; change it only to hand over. */}
        <select
          className={SELECT}
          value={report.owner}
          disabled={busy}
          aria-label={`Owner for: ${report.title}`}
          onChange={(e) => onPatch({ owner: e.target.value as OwnerId })}
        >
          {owners.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name.split(' ')[0]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <select
          className={SELECT}
          value={report.status}
          disabled={busy}
          aria-label={`Status for: ${report.title}`}
          onChange={(e) => onPatch({ status: e.target.value as FeedbackReport['status'] })}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        {/* The exact post, not the channel — the whole point of storing the message ts. */}
        <a
          href={report.permalink || undefined}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open in Slack: ${report.title}`}
          className={`inline-flex size-7 items-center justify-center ${RADIUS} border border-zinc-200 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 ${
            report.permalink ? '' : 'pointer-events-none opacity-40'
          }`}
        >
          <ExternalLink className="size-3.5" aria-hidden />
        </a>
      </div>
    </div>
  );
}
